"use client";

import React from "react";
import clsx from "clsx";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { CHAT_ENTITY_CARD_ICON_CLASS, CHAT_ENTITY_CARD_SHELL_CLASS } from "./entityPreviewUi";

type ChatEntityCardShellProps = {
  icon: IconDefinition;
  title: string;
  meta?: React.ReactNode;
  testId?: string;
  children?: React.ReactNode;
  className?: string;
  isExpanded?: boolean;
  onExpand?: () => void;
  trailing?: React.ReactNode;
};

export function ChatEntityCardShell({
  icon,
  title,
  meta,
  testId,
  children,
  className,
  isExpanded = true,
  onExpand,
  trailing,
}: ChatEntityCardShellProps) {
  const header = (
    <>
      <div className="flex min-w-0 items-center gap-1.5">
        <FontAwesomeIcon icon={icon} className={CHAT_ENTITY_CARD_ICON_CLASS} aria-hidden />
        <span className="truncate text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">
          {title}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {meta ? (
          <span className="text-[11px] leading-tight text-slate-500 dark:text-slate-400">{meta}</span>
        ) : null}
        {trailing}
        {!isExpanded ? (
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 text-slate-400 dark:text-slate-500" aria-hidden />
        ) : null}
      </div>
    </>
  );

  if (!isExpanded) {
    return (
      <button
        type="button"
        data-testid={testId}
        data-minimized="true"
        aria-expanded={false}
        aria-label={`Expand ${title}`}
        onClick={onExpand}
        className={clsx(
          CHAT_ENTITY_CARD_SHELL_CLASS,
          "flex w-full items-center justify-between gap-2 px-0.5 py-0.5 text-left transition-colors hover:border-slate-300/80 hover:bg-white/90 dark:hover:border-slate-600/65 dark:hover:bg-slate-900/40",
          className,
        )}
      >
        {header}
      </button>
    );
  }

  return (
    <div
      data-testid={testId}
      data-minimized="false"
      aria-expanded={true}
      className={clsx(CHAT_ENTITY_CARD_SHELL_CLASS, "space-y-1.5", className)}
    >
      <div className="flex items-center justify-between gap-2 px-0.5">{header}</div>
      {children}
    </div>
  );
}
